/**
 * Thirty days of sending, drawn — and the one thing this slice was deferred
 * five slices to get right.
 *
 * ## A CHART OVER NO DATA MUST NOT DRAW A FLAT LINE AT ZERO
 *
 * Slice 0 left the Overview a placeholder and said why, and slice 10 quoted it
 * back: *inventing a dashboard out of numbers that do not exist is how a demo
 * starts lying.* A chart is the sharpest form of that lie, because a chart
 * interpolates. Given thirty absent days it draws a confident flat line along
 * the baseline, and that line is what a healthy quiet week, an unconnected
 * channel and a workspace that has never sent **all look like** — three states
 * whose repairs are entirely different.
 *
 * Nothing in this file defends against that by remembering to. The defence is
 * three shapes, and every one of them is somebody else's:
 *
 *   1. **A channel with nothing to draw is `measured: false`** and has no bucket
 *      array at all, so there is nothing here to map over. It arrives with its
 *      own sentence naming which of the three it is — `channel_not_connected`,
 *      `nothing_sent_ever`, `nothing_sent_in_window` — and that sentence is
 *      printed where the chart would have been.
 *   2. **A day before the workspace existed is an arm with no count field**, so
 *      `emailDailyValues` maps it to `null` and there is no `?? 0` that could
 *      reach it. `TimeSeriesChart` draws no mark for a `null` and does not join
 *      a line across it; the axis label stays, because a reader comparing to a
 *      calendar needs the day to be there.
 *   3. **`chartAxisMax` returns `null` when there is nothing to scale**, and a
 *      chart handed `null` draws its empty state rather than inventing an axis.
 *
 * The middle one is the interesting distinction and it is worth being explicit
 * about: **`null` and `0` are different marks.** A day the workspace existed for
 * and sent nothing on is a real zero and is drawn as one — "nothing sent on
 * Tuesday" is a true statement about Tuesday. A day before the workspace existed
 * is not a zero; it is a day there was nothing to say anything about.
 *
 * ## AND THE SAME RULE POINTED THE OTHER WAY, WHICH THIS FILE GOT WRONG ONCE
 *
 * Three defences all guard one direction: *do not say measured about something
 * unmeasured*. The inverse got through them, and it got through because it looks
 * like the safe answer. `chartAxisMax` returns `null` for a series that is every
 * day zero with no threshold over it — and exactly one chart here has no
 * threshold, because Meta publishes no rate. So a workspace that sent WhatsApp
 * for three weeks and had **nothing** fail hit the empty state and was told
 * *"Nothing to chart yet"*, which is the sentence for a channel nobody has
 * connected.
 *
 * That is the same confusion of zero and unmeasured, in the direction nobody
 * watches, on the reader with the least other signal to check it against: their
 * month was clean, and the screen said it did not know.
 * {@link WHATSAPP_NONE_UNDELIVERABLE_COPY} is what a measured, all-zero series
 * says instead. The email charts once needed no counterpart, because Amazon's
 * line was above zero on every day and so they always scaled; since the line is
 * drawn only on a day that clears the rate floor, a clean low-volume month has
 * nothing to scale by either, and says {@link EMAIL_NONE_REPORTED_COPY}.
 *
 * ## AND A FOURTH THING A ZERO CAN BE, WHICH ONLY THE LIVE DEPLOYMENT SHOWED
 *
 * The three defences above are about days and channels. None of them asked
 * whether a *measured* channel's outcome counts had been reported by anybody.
 * On the deployment that actually sends, SNS is not wired and no delivery event
 * has ever arrived, so every accepted email sits in `awaiting` and every
 * `bounced` and `complained` is `0`. The threshold line gave the chart a scale,
 * so it drew a month of zero stubs under Amazon's dashed line — the flat line
 * at zero, arriving through the arm with numbers in it — and the WhatsApp
 * chart's all-zero branch would have said nothing failed.
 *
 * Three changes. `emailDailyValues` and `whatsAppDailyValues` answer `null`,
 * not `0`, for an outcome on a day none of whose messages has a report — which
 * on its own does not refuse such a month, because a quiet day in it is still a
 * true zero and a chart with one zero has something to draw. So each outcome
 * chart also refuses a window with no reported day, and this file asks
 * `noOutcomeReported` of each measured channel's totals before mounting one at
 * all, printing {@link NO_OUTCOME_REPORTED_COPY} where the charts would have
 * been — an empty chart frame with "Nothing to chart yet" in it would have been
 * true and useless, because it does not say *why*, and the why is the whole
 * message. Where Amazon reported spam complaints and nothing else, the sentence
 * is `NO_OUTCOME_BUT_COMPLAINTS_COPY`, which does not say none came back. The settling note is withheld for such a channel too: "they fill in
 * as the reports arrive" is a promise about a report stream that, on that
 * deployment, is not running.
 *
 * ## THE BARS ARE UTC DAYS AND THE AXIS SAYS SO
 *
 * Every bucket is one UTC day, cut on `messages.sent_at`. Analytics elsewhere in
 * this app resolves a range in a zone the tenant picks, which is right for a
 * report somebody chose the boundaries of and wrong here, where nobody chose
 * anything. The alternative — the browser's own zone — means the same workspace
 * shows different bars to a colleague in London and a colleague in Mumbai with
 * nothing on screen saying so.
 *
 * So the x-axis title is `bucketing.axisLabel`, off the response, and
 * {@link shortUtcDay} formats every tick **with `timeZone: 'UTC'` explicitly
 * set**. That is not belt and braces: `lib/format.ts` renders every other date
 * in this product in the viewer's own zone, and a chart that borrowed it would
 * label a bar "12 Sep" that starts at 05:30 on the 12th in India. One function
 * for both would be one function telling two different truths.
 *
 * ## THE THRESHOLD IS A COUNT, BECAUSE A DAILY RATE WOULD BE A LIE
 *
 * There is no percentage anywhere on these charts and there is nowhere on the
 * contract to put one. `OVERVIEW_MIN_VOLUME_FOR_RATE` is a thousand reported
 * outcomes; a workspace that clears it over the whole window averages
 * thirty-three a day, so thirty honest daily rates would be thirty nulls and the
 * pressure would immediately be to lower the floor "just for the chart".
 *
 * What is drawn instead is Amazon's line **in messages**: `dailyThresholdCount`
 * is 5% of that day's reported deliveries and bounces, so the line rises and
 * falls with them — sixty bounces on a day of twelve hundred reported.
 *
 * **And only on a day that clears the same floor.** The first version drew it on
 * every day, and argued that a line at 0.15 over a three-message day was the
 * true version of a "33%" label. Review held the picture to the page's own rule:
 * a bar ten times the height of a red line labelled "Amazon reviews above 5%"
 * says *over the line* as plainly as a percentage would, one card below a tile
 * that says a rate needs a thousand messages. A line is a rate drawn. So a day
 * under the floor, or with no report back, gets its count and no line, and a
 * chart with no line on any day says {@link EMAIL_CHART_THRESHOLD_FLOOR_COPY}.
 *
 * **The WhatsApp chart has no threshold line and one must not be added out of
 * symmetry.** Meta publishes no rate: a number's quality rating falls when
 * messages are not delivered, and Meta says neither at what rate nor over what
 * window. Slice 18's `WHATSAPP_SEND_RISK_GATE.haltRate` is a real line this
 * product enforces, over *settled* outcomes per campaign — and settled is not
 * accepted, which is the decision slice 18 turns on. Drawing it against a day's
 * accepted count would put back the denominator slice 18 deleted.
 * {@link WHATSAPP_NO_THRESHOLD_COPY} says so on the chart.
 *
 * ## WHICH CHARTS, AND WHY NOT MORE
 *
 * Two questions, and the charts are one each:
 *
 *   - **Am I sending?** One chart, both channels, `accepted` per day, in
 *     `CHANNEL_SERIES_TONE`'s colours so email is the same colour here as it is
 *     anywhere else on the screen. Above the fold.
 *   - **Is anything going wrong?** Bounces per day and spam complaints per day,
 *     each against its own Amazon line, plus WhatsApp's undeliverable count.
 *     Below the fold, because a reader goes looking for these *after* a rate at
 *     the top of the page has told them to.
 *
 * A delivered-per-day series was rejected. `delivered ⊆ accepted` and the gap
 * between them is already a tile with a denominator big enough to mean
 * something; drawn as a second bar it invites the eye to read the gap as a daily
 * deliverability rate, over a denominator of eleven messages. `complained` is
 * never a stack segment for the harder version of the same reason: a complaint
 * follows a delivery, so `delivered + bounced + complained` counts some messages
 * twice and produces a bar taller than the day's own accepted count.
 *
 * ## PROGRESSIVE, NOT DELETED
 *
 * Four things about these bars are not guessable from them: the zone, the two
 * short bars at the ends, the recent days still settling, and why there is no
 * percentage. Each has a short version printed under the chart and a full
 * sentence one click away in a `MoreDetail` under each card's charts — one per
 * card rather than one per chart, because four disclosures on one card is
 * furniture and nobody opens the fourth. One per card and not one for the page:
 * the volume card's used to live only in the outcomes card, which is not mounted
 * when nothing has been reported, so on the live deployment the UTC and
 * partial-day explanations were nowhere (review).
 *
 * Slice 16 once read "progressive" as "deleted" and left a screen with three
 * fields and no instructions. The detail is in the DOM whether the disclosure is
 * open or not, which is what `MoreDetail` is for.
 */
import {
  AWAITING_OUTCOMES_COPY,
  BEFORE_WORKSPACE_COPY,
  CHANNEL_SERIES_TONE,
  CHART_EMPTY_COPY,
  CHART_FIGURES_COPY,
  EMAIL_CHART_THRESHOLD_FLOOR_COPY,
  EMAIL_NONE_REPORTED_COPY,
  NO_DAILY_RATE_COPY,
  EMAIL_DAILY_MEASURE_LABELS,
  OVERVIEW_ACTIVITY_TIME_ZONE_COPY,
  OVERVIEW_ACTIVITY_WINDOW_DAYS,
  OVERVIEW_MIN_VOLUME_FOR_RATE,
  OVERVIEW_RATE_THRESHOLDS,
  healthBand,
  rateReading,
  type HealthBand,
  type RateReading,
  OVERVIEW_RECENT_DAYS,
  PARTIAL_DAY_COPY,
  RECENT_DAYS_COPY,
  WHATSAPP_DAILY_MEASURE_LABELS,
  WHATSAPP_NONE_UNDELIVERABLE_COPY,
  WHATSAPP_NO_THRESHOLD_COPY,
  chartAxisMax,
  emailChartSeries,
  emailDailyValues,
  emailThresholdLine,
  noOutcomeReported,
  whatsAppChartSeries,
  whatsAppDailyValues,
  type ChartSeries,
  type DeliveryReports,
  type EmailDailyBucket,
  type OverviewActivityResponse,
  type OverviewResponse,
  type WhatsAppDailyBucket,
} from '@wend/shared';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  NoFigure,
  SegmentedToggle,
  Spinner,
  StateChip,
  TimeSeriesChart,
  cn,
} from '@wend/ui';
import { useState, type ReactNode } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { unreportedExplanation, type UnreportedExplanation } from '../../lib/delivery-reports-display';
import { formatCount, formatDateTime } from '../../lib/format';
import { formatRate } from './overview-format';
import { BAND_TEXT, BAND_VERDICT, ThresholdBar } from './OverviewFigures';
import type { AsyncData } from '../../lib/use-async-data';
import { MoreDetail } from '../templates/MoreDetail';

/* -------------------------------------------------------------------------- */
/* Days, in the zone the bars were cut in                                      */
/* -------------------------------------------------------------------------- */

/**
 * `2026-09-12` → `12 Sep`, **in UTC and never in the viewer's zone**.
 *
 * The `timeZone` option is the whole point of this function existing rather than
 * `formatDate` being reused. See the file header.
 */
const DAY_LABEL = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

function shortUtcDay(date: string): string {
  return DAY_LABEL.format(new Date(`${date}T00:00:00.000Z`));
}

/**
 * The sentences that apply to *these* bars, and none that do not.
 *
 * Each one is conditional on the thing it describes actually being in the data.
 * A standing "some of these days are partial" under a chart where none of them
 * are is how a reader learns that the sentences under a chart are boilerplate,
 * and then does not read the one that matters.
 *
 * `settling` is whether the settling note may be said at all. It is withheld
 * for a channel whose outcomes nobody has reported — "Recent days are still
 * settling" over a month of messages with no report is the reassuring version of
 * the wrong fact — and for the volume chart, whose bars are accepted counts and
 * do not change when a report arrives. Review found it printed under both.
 */
function chartNotes(
  buckets: readonly (EmailDailyBucket | WhatsAppDailyBucket)[],
  settling: boolean,
): readonly string[] {
  const notes: string[] = [OVERVIEW_ACTIVITY_TIME_ZONE_COPY.short];
  if (buckets.some((bucket) => bucket.state === 'before_workspace')) {
    notes.push(BEFORE_WORKSPACE_COPY.short);
  }
  if (buckets.some((bucket) => bucket.state !== 'before_workspace' && bucket.coverage === 'partial_day')) {
    notes.push(PARTIAL_DAY_COPY.short);
  }
  if (settling && buckets.some((bucket) => bucket.state === 'activity' && bucket.awaiting > 0)) {
    notes.push(AWAITING_OUTCOMES_COPY.short);
  }
  return notes;
}

function Notes({ notes }: { notes: readonly string[] }) {
  return (
    <p className="text-[0.76rem] leading-relaxed text-muted">{notes.join(' ')}</p>
  );
}

/* -------------------------------------------------------------------------- */
/* Am I sending?                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Accepted per day, both channels on one scale.
 *
 * The two `measured` arms are read independently, so a workspace that sends only
 * email gets one series and no WhatsApp apology, and a workspace on both gets
 * two bars per day in the colours those channels wear everywhere else. When
 * neither channel is measured there is no chart at all: the two sentences are
 * printed instead, **separately**, because "SES is not connected" and "no
 * WhatsApp message has ever been sent" send the reader to different places and a
 * merged sentence makes them work out which one they are looking at.
 */
export function VolumeChart({
  activity,
  deliveryReports,
}: {
  activity: OverviewActivityResponse;
  /** The Overview response's own fact, so this card and the tiles word one silence one way. */
  deliveryReports: DeliveryReports | null;
}) {
  const { email, whatsapp } = activity.channels;

  const series: ChartSeries[] = [];
  if (email.measured) {
    series.push(
      emailChartSeries({
        buckets: email.buckets,
        measure: 'accepted',
        tone: CHANNEL_SERIES_TONE.email,
        pattern: 'solid',
      }),
    );
  }
  if (whatsapp.measured) {
    series.push(
      whatsAppChartSeries({
        buckets: whatsapp.buckets,
        measure: 'accepted',
        tone: CHANNEL_SERIES_TONE.whatsapp,
        pattern: 'hatched',
      }),
    );
  }

  if (series.length === 0) {
    return (
      <div className="flex flex-col gap-2 rounded-base border border-dashed border-line px-5 py-8">
        {!email.measured && (
          <p className="text-[0.85rem] leading-relaxed text-muted">{email.message}</p>
        )}
        {!whatsapp.measured && (
          <p className="text-[0.85rem] leading-relaxed text-muted">{whatsapp.message}</p>
        )}
      </div>
    );
  }

  /* Both channels are bucketed over the same window by the same function, so
     either array names the same days. Email first only because it is the
     channel this product leads with. */
  const buckets: readonly (EmailDailyBucket | WhatsAppDailyBucket)[] = email.measured
    ? email.buckets
    : whatsapp.measured
      ? whatsapp.buckets
      : [];
  const emailUnreported = email.measured && noOutcomeReported(email.totals);
  const whatsappUnreported = whatsapp.measured && noOutcomeReported(whatsapp.totals);
  const volumeNotes = chartNotes(buckets, false);

  return (
    <div className="flex flex-col gap-3">
      <TimeSeriesChart
        label="Messages accepted per day, by channel"
        series={series}
        xLabels={buckets.map((bucket) => shortUtcDay(bucket.date))}
        xAxisLabel={activity.bucketing.axisLabel}
        axisMax={chartAxisMax(series)}
        emptyMessage={CHART_EMPTY_COPY.short}
        figures={CHART_FIGURES_COPY}
      />
      {/* No settling note: these bars are accepted counts, and a report
          arriving does not change one of them. */}
      <Notes notes={volumeNotes} />
      <ActivityDetail notes={volumeNotes} hasThresholds={false} hasWhatsAppChart={false} />
      {/* Accepted is true either way; what the bars cannot say is that nobody
          has reported on them. Said here too, because this is the chart a
          reader sees without scrolling. */}
      {/*
        A CHIP IN FRONT, THE SENTENCE ONE LEVEL DOWN — AND IT STAYS ON THIS CARD.

        These were two warn-coloured sentences. What a reader needs at a glance
        is three words: these bars are accepted counts nobody has reported back
        on. So the chip is the visible carrier.

        The sentence itself does **not** move to the outcomes card below, which
        is where it would naturally belong. A review already found that: in this
        state the outcomes card's box is not mounted, so this card was the only
        place the sentence lived, and `verify-dashboard` asserts it is here.
        It goes into a disclosure instead, whose children are in the DOM while
        closed — so the assertion still means something, a reader searching the
        page still finds it, and a screen reader still walks it.
      */}
      {(emailUnreported || whatsappUnreported) && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {emailUnreported && <StateChip state="not_measured" label="Email — no reports yet" />}
            {whatsappUnreported && (
              <StateChip state="not_measured" label="WhatsApp — no reports yet" />
            )}
          </div>
          {/*
            THE WHOLE EXPLANATION, NOT JUST ITS FIRST SENTENCE — SLICE 26.

            It carried only `.title` per channel. That was enough while the
            outcomes card was also on the page carrying the paragraphs; slice 26
            stopped mounting that card in the state where it drew nothing, and
            `NO_OUTCOME_REPORTED_COPY.whatsapp.detail` would then have had
            nowhere on the Overview left to be said. Hiding a card is allowed to
            remove a duplicate and is not allowed to remove the only copy.
          */}
          <MoreDetail summary="What these bars cannot say">
            {emailUnreported && (
              <UnreportedLines
                explanation={unreportedExplanation('email', deliveryReports, email.totals.complained)}
              />
            )}
            {whatsappUnreported && (
              <UnreportedLines explanation={unreportedExplanation('whatsapp', deliveryReports, 0)} />
            )}
          </MoreDetail>
        </div>
      )}
      {/* One channel measured and the other not is the common case, and the
          unmeasured one still gets its own sentence — under the chart rather
          than instead of it. */}
      {!email.measured && (
        <p className="text-[0.76rem] leading-relaxed text-muted">{email.message}</p>
      )}
      {!whatsapp.measured && (
        <p className="text-[0.76rem] leading-relaxed text-muted">{whatsapp.message}</p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Is anything going wrong?                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One email outcome chart's series, Amazon's line and the scale they make —
 * here once, because the card asks the question the chart does ("will anything
 * be drawn?") before it prints notes about bars, and two copies of that
 * arithmetic would drift.
 *
 * The line is drawn only on a day that clears the rate floor and has a report
 * back — see `dailyThresholdCount`. When no day does, the chart is given no line
 * at all: a chart named "against Amazon's line" with no line on it sends the
 * reader looking for it.
 */
function emailOutcomeScale(
  buckets: readonly EmailDailyBucket[],
  measure: 'bounced' | 'complained',
  which: 'bounce' | 'complaint',
) {
  const series = [
    emailChartSeries({ buckets, measure, tone: CHANNEL_SERIES_TONE.email, pattern: 'solid' }),
  ];
  const threshold = emailThresholdLine(buckets, which);
  const lineDrawn = threshold.values.some((value) => value !== null);
  const thresholds = lineDrawn ? [threshold] : [];
  return { series, lineDrawn, thresholds, axisMax: chartAxisMax(series, thresholds) };
}

function EmailOutcomeChart({
  buckets,
  measure,
  which,
  heading,
  accessibleName,
  reportedTotal,
  deliveryReports,
}: {
  buckets: readonly EmailDailyBucket[];
  measure: 'bounced' | 'complained';
  which: 'bounce' | 'complaint';
  /** The visible `<h3>`. Short — the card already says which channel. */
  readonly heading: string;
  /**
   * The figure's accessible name, and deliberately **not** `heading`.
   * `TimeSeriesChart` renders its `label` `sr-only`, so passing the visible
   * heading again would have a screen reader say "Bounced — email" twice in a
   * row. This says the thing the heading leaves to the eye: what the dashed
   * line across it is — and, for a window where no day carries one, that there
   * is none.
   */
  readonly accessibleName: { readonly withLine: string; readonly withoutLine: string };
  /**
   * The window's reported total of `measure`, so a chart with nothing to scale
   * by does not say "none" while a count exists — "none has been reported as
   * spam" beside "Complaints 1" was a contradiction (review). A complaint on a
   * day with nothing else reported is drawn since slice 19's review, and this
   * still guards any day the chart cannot draw.
   */
  readonly reportedTotal: number;
  /** For the sentence this chart says instead of drawing, when no day has a report. */
  readonly deliveryReports: DeliveryReports | null;
}) {

  /*
   * ITS OWN GATE, NOT ONLY THE CARD'S. A quiet day is a true zero, so a month
   * whose every sending day is unreported still reaches the chart as zeros and
   * nulls, and `hasDrawableValues` counts the zeros: handed such a month, this
   * drew a row of zero stubs. The card asks `noOutcomeReported` before mounting
   * it, and a comment used to say the chart refused the month too; it did not.
   * Now it does, so the next screen that mounts it cannot draw that month.
   */
  if (!hasReportedDay(buckets)) {
    return (
      <NoOutcomeReported
        explanation={unreportedExplanation('email', deliveryReports, complaintsIn(buckets))}
      />
    );
  }

  const { series, lineDrawn, thresholds, axisMax } = emailOutcomeScale(buckets, measure, which);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <h3 className="text-[0.9rem] font-semibold tracking-[-0.015em] text-text">{heading}</h3>
      <TimeSeriesChart
        label={lineDrawn ? accessibleName.withLine : accessibleName.withoutLine}
        series={series}
        thresholds={thresholds}
        xLabels={buckets.map((bucket) => shortUtcDay(bucket.date))}
        xAxisLabel={OVERVIEW_ACTIVITY_TIME_ZONE_COPY.axisLabel}
        axisMax={axisMax}
        /* Past the gate above some day has a report, so its count is a number
           and a null scale means every count drawn is zero with no line over it
           — a clean month, not an empty one, unless the window's total says a
           report landed on a day the chart cannot draw. The same mirror image
           the WhatsApp chart below explains, reachable here since the floor. */
        emptyMessage={reportedTotal > 0 ? CHART_EMPTY_COPY.short : EMAIL_NONE_REPORTED_COPY[which]}
        figures={CHART_FIGURES_COPY}
      />
      {/* Why there is no line is said once, under both email charts, by the
          card — see EMAIL_CHART_THRESHOLD_FLOOR_COPY. */}
    </div>
  );
}

/**
 * Whether any day in the window has an outcome reported — delivered, bounced or
 * failed — rather than every accepted message still waiting. The per-chart form
 * of `noOutcomeReported`, over days rather than over the window's totals.
 */
function hasReportedDay(buckets: readonly (EmailDailyBucket | WhatsAppDailyBucket)[]): boolean {
  return buckets.some((bucket) => bucket.state === 'activity' && bucket.awaiting < bucket.accepted);
}

/** Spam complaints reported across a series' days — the one outcome counted on an unreported day. */
function complaintsIn(buckets: readonly EmailDailyBucket[]): number {
  return buckets.reduce((total, bucket) => total + (bucket.state === 'activity' ? bucket.complained : 0), 0);
}

/**
 * Where the outcome charts would have been, for a channel with nothing reported.
 * Every sentence visible: this is the only thing that half of the card has to
 * say, and a disclosure around it would hide the whole message.
 *
 * The words are `unreportedExplanation`'s, asked with the Overview response's
 * `deliveryReports` — the tiles' own. The card had its own hedge, "probably a
 * setup step", under tiles saying flatly that no report had ever arrived: two
 * explanations of one silence on one page (review).
 */
function NoOutcomeReported({ explanation }: { explanation: UnreportedExplanation }) {
  /*
   * SLICE 25: ONE SENTENCE ON SCREEN, THE REST ONE LEVEL DOWN.
   *
   * This block was measured at 172 words and 9 sentences with no figure and no
   * chart in it, printed four times across the Overview — about 380 of the
   * page's 1,489 words for one fact. The fact is not in dispute and is not
   * softened: the chip names the state, the card's own title still says it in
   * a sentence, and every paragraph is kept in a disclosure whose children stay
   * in the DOM while it is closed, so a reader searching the page for
   * "suppression" or "SNS" still finds them.
   *
   * **The brief asked for a hatched plot area here and it is deliberately not
   * drawn.** `TimeSeriesChart` hatches a slot where every series is silent, but
   * that is inside a chart that has a scale. A band across a whole plot area
   * with nothing measured would need an axis maximum nobody measured, and
   * inventing one is the exact failure that component's header calls "the
   * flat-empty-chart failure arriving through the one array nobody thinks of as
   * data". The frame is kept as the dashed empty box the chart itself draws in
   * that state; the chip does the work the hatching was meant to do.
   */
  return (
    <div
      data-state="no-outcome-reported"
      data-reports={explanation.state}
      className="flex flex-col gap-2 rounded-base border border-dashed border-warn/40 px-5 py-6"
    >
      <div className="flex flex-wrap items-center gap-2">
        <StateChip state="not_measured" label="No reports yet" />
      </div>
      <p className="max-w-prose text-[0.9rem] font-medium text-text">{explanation.title}</p>
      <MoreDetail summary="Why there is nothing to chart">
        {explanation.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </MoreDetail>
    </div>
  );
}

/**
 * One channel's whole no-report explanation: the sentence and every paragraph
 * behind it, as the contract wrote them.
 *
 * Used inside a disclosure, so all of it is in the DOM whether or not anybody
 * opens it — which is what keeps these sentences findable by Ctrl+F and readable
 * in order by a screen reader.
 */
function UnreportedLines({ explanation }: { explanation: UnreportedExplanation }) {
  return (
    <>
      <p>{explanation.title}</p>
      {explanation.paragraphs.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </>
  );
}

function whatsAppUndeliverableSeries(buckets: readonly WhatsAppDailyBucket[]) {
  return whatsAppChartSeries({
    buckets,
    measure: 'undeliverable',
    tone: CHANNEL_SERIES_TONE.whatsapp,
    pattern: 'hatched',
  });
}

function WhatsAppOutcomeChart({
  buckets,
  deliveryReports,
}: {
  buckets: readonly WhatsAppDailyBucket[];
  deliveryReports: DeliveryReports | null;
}) {
  const series = [whatsAppUndeliverableSeries(buckets)];

  /*
   * THE EMPTY STATE'S MIRROR IMAGE, AND IT IS THE ONE THIS FILE GOT WRONG.
   *
   * This is the only chart on the dashboard with no threshold line, so it is the
   * only one whose scale comes from the bars alone — and `chartAxisMax` returns
   * `null` for a series that is every-day zero. A workspace that sent WhatsApp
   * for three weeks and had nothing fail lands here, and the first version of
   * this file then printed `CHART_EMPTY_COPY.short`: *"Nothing to chart yet"*,
   * which is the sentence for a channel nobody has connected.
   *
   * That is this slice's own rule broken in the direction nobody watches. A flat
   * line at zero says "measured" about something unmeasured; this said
   * "unmeasured" about a perfect month. Both are the same confusion and the
   * second is worse here, because the reader whose sending is clean is the one
   * with no other signal to check it against.
   *
   * `hasReportedDay` is asked as well as `axisMax`, rather than trusting the
   * caller only to mount this for a channel with reports: "none failed" is a
   * claim about messages Meta reported on. It used to ask whether any value was
   * non-null, and a quiet day is a true zero — so a WhatsApp channel Meta had
   * never reported on, with one quiet day in the window, was told none of its
   * messages failed. Review found it. With no reported day the chart is not drawn
   * at all, the same gate the email charts keep.
   */
  if (!hasReportedDay(buckets)) {
    return <NoOutcomeReported explanation={unreportedExplanation('whatsapp', deliveryReports, 0)} />;
  }
  const axisMax = chartAxisMax(series);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <h3 className="text-[0.9rem] font-semibold tracking-[-0.015em] text-text">
        Could not be delivered — WhatsApp
      </h3>
      <TimeSeriesChart
        label="WhatsApp messages Meta reported as failed, per day. No threshold line — Meta publishes no rate."
        series={series}
        xLabels={buckets.map((bucket) => shortUtcDay(bucket.date))}
        xAxisLabel={OVERVIEW_ACTIVITY_TIME_ZONE_COPY.axisLabel}
        axisMax={axisMax}
        emptyMessage={
          axisMax === null ? WHATSAPP_NONE_UNDELIVERABLE_COPY : CHART_EMPTY_COPY.short
        }
        figures={CHART_FIGURES_COPY}
      />
      {/* About a line over bars; with nothing drawn there is nothing to say
          it about, and its full sentence is only mounted where bars are. */}
      {axisMax !== null && (
        <p className="text-[0.76rem] leading-relaxed text-muted">
          {WHATSAPP_NO_THRESHOLD_COPY.short}
        </p>
      )}
    </div>
  );
}

/**
 * The full detail, once, at the foot of the section.
 *
 * Everything above prints the four-word version; this is the paragraph behind
 * it. It is one disclosure rather than one per chart because a card with four
 * of them is a card with none — and the children are in the DOM while it is
 * closed, so Ctrl+F still finds the sentence about Amazon's line.
 *
 * **The same notes as the four-word line above it, and no others.** It used to
 * print every paragraph every time: a workspace a year old was told it had been
 * created part-way through the period, and a channel nobody had reported on was
 * promised its bars would fill in — the exact sentences `chartNotes` checks the
 * data before saying. So the paragraphs are chosen by the same notes.
 */
function ActivityDetail({
  notes,
  hasThresholds,
  hasWhatsAppChart,
}: {
  notes: readonly string[];
  hasThresholds: boolean;
  hasWhatsAppChart: boolean;
}) {
  return (
    <MoreDetail summary="What these bars do and do not include">
      <p>{OVERVIEW_ACTIVITY_TIME_ZONE_COPY.detail}</p>
      {notes.includes(PARTIAL_DAY_COPY.short) && <p>{PARTIAL_DAY_COPY.detail}</p>}
      {notes.includes(AWAITING_OUTCOMES_COPY.short) && <p>{AWAITING_OUTCOMES_COPY.detail}</p>}
      {notes.includes(BEFORE_WORKSPACE_COPY.short) && <p>{BEFORE_WORKSPACE_COPY.detail}</p>}
      {hasThresholds && <p>{NO_DAILY_RATE_COPY.detail}</p>}
      {hasWhatsAppChart && <p>{WHATSAPP_NO_THRESHOLD_COPY.detail}</p>}
    </MoreDetail>
  );
}

/* -------------------------------------------------------------------------- */
/* Last 7 days                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The trailing slice of the same daily buckets the charts below draw.
 *
 * WHY IT COUNTS ONLY WHAT A PROVIDER ACCEPTED
 * ---------------------------------------------------------------------------
 * `OUTCOME_MEASURES_KNOWN_WITHOUT_A_REPORT` is `['accepted', 'awaiting']`.
 * Everything else — delivered, bounced, complained, undeliverable — is a
 * *report*, and `emailDailyValues` answers `null` for it on a day nothing has
 * been reported for. On the deployment that actually sends, no report of any
 * kind has ever arrived, so a seven-day bounce or spam figure here would be
 * seven nulls wearing a heading: the "no reports yet" card slice 26 deleted,
 * rebuilt smaller.
 *
 * Restricting the card to `accepted` is not a simplification, it is the safety
 * property. It leaves exactly **one** absent state — `before_workspace`, the
 * arm that carries no count field at all — so there is no branch of this card
 * in which a figure nobody measured can be printed as a number. That is
 * structural, the way `NoFigure` having no `value` prop is structural, rather
 * than a thing each future edit has to remember.
 *
 * WHY THERE ARE NO MARKS
 * ---------------------------------------------------------------------------
 * The obvious drawing is seven little bars. That is `Sparkline`, which slice 25
 * deleted with its eight assertions, and the second of the two reasons it gave
 * applies here word for word: *the only series it could honestly carry is
 * already drawn one card below with an axis and a table.* Seven marks here ARE
 * the last seven bars of the volume chart, smaller and without an axis.
 *
 * WHAT IT DOES NOT READ, AND WHY EACH WOULD BE WRONG
 * ---------------------------------------------------------------------------
 * - `channels.*.totals` — those are the **thirty**-day totals, and
 *   `emailDailySeriesSchema` pins `totals.accepted` to `GET /overview`'s
 *   `sending.accepted`. Printing one under a seven-day heading is a thirty-day
 *   number in the wrong label, and it is the single most likely way to get this
 *   card wrong. A control assertion exists for exactly that.
 * - `window`, `workspaceCreatedAt` — the `before_workspace` arm already encodes
 *   what they would say, and a second date needs a second formatter, which is a
 *   second chance to print a UTC day as the reader's day.
 * - `series.message` / `series.reason` — long sentences the volume card already
 *   prints one card below.
 *
 * The slice is only sound because `enumerateUtcDays` emits an entry for every
 * UTC day the window touches, gaps included, so a contiguous trailing run is a
 * complete sub-window and not a sample. A `GROUP BY` result would not be.
 */
function totalOf(values: readonly (number | null)[]): number | null {
  /*
   * THE PARAMETER TYPE IS THE GUARD, NOT THE COMMENT.
   *
   * It takes the MAPPER'S output, `(number | null)[]`, so a bucket array is not
   * assignable and this function cannot be handed `bucket[measure]` directly.
   * That matters because on a workspace with no delivery reports every raw
   * outcome field on every day is a `0` meaning "nobody reported", and adding
   * that to a `0` meaning "a quiet day" is the defect the whole contract file
   * exists to refuse. `complaintsIn` above is the only other count-fold here,
   * and the next person to write one will reach for the obvious spelling.
   *
   * All-null totals to `null`, not to `0`: those are days the workspace did not
   * exist for, and a workspace that did not exist did not send nothing.
   */
  const known = values.filter((value): value is number => value !== null);
  return known.length === 0 ? null : known.reduce((sum, value) => sum + value, 0);
}

interface RecentRow {
  readonly label: string;
  readonly total: number | null;
  readonly values: readonly (number | null)[];
}

export function recentRows(data: OverviewActivityResponse): readonly RecentRow[] {
  const rows: RecentRow[] = [];
  if (data.channels.email.measured) {
    const values = emailDailyValues(data.channels.email.buckets, 'accepted').slice(-OVERVIEW_RECENT_DAYS);
    rows.push({ label: EMAIL_DAILY_MEASURE_LABELS.accepted, total: totalOf(values), values });
  }
  if (data.channels.whatsapp.measured) {
    const values = whatsAppDailyValues(data.channels.whatsapp.buckets, 'accepted').slice(-OVERVIEW_RECENT_DAYS);
    rows.push({ label: WHATSAPP_DAILY_MEASURE_LABELS.accepted, total: totalOf(values), values });
  }
  return rows;
}

/** The trailing seven days of whichever channel is measured, for the table. */
function recentDays(data: OverviewActivityResponse): readonly (EmailDailyBucket | WhatsAppDailyBucket)[] {
  const buckets = data.channels.email.measured
    ? data.channels.email.buckets
    : data.channels.whatsapp.measured
      ? data.channels.whatsapp.buckets
      : [];
  return buckets.slice(-OVERVIEW_RECENT_DAYS);
}

const WINDOW_CHOICES = [
  { key: 'day', label: 'Day', days: 1, heading: 'Last 24 hours' },
  { key: 'week', label: 'Week', days: OVERVIEW_RECENT_DAYS, heading: `Last ${String(OVERVIEW_RECENT_DAYS)} days` },
  { key: 'month', label: 'Month', days: OVERVIEW_ACTIVITY_WINDOW_DAYS, heading: `Last ${String(OVERVIEW_ACTIVITY_WINDOW_DAYS)} days` },
] as const;

type WindowKey = (typeof WINDOW_CHOICES)[number]['key'];

/**
 * The fold for a measure that is a REPORT, and it is not `totalOf`.
 *
 * `totalOf` skips nulls, which is right for `accepted`: a null there is a day
 * before the workspace existed, and a day that could not have sent anything
 * contributes nothing to a sum. A null in `delivered`, `bounced` or
 * `complained` means something completely different — **nobody has reported on
 * that day yet** — and skipping it silently asserts it delivered nothing.
 *
 * This was not hypothetical. The first version of this panel used `totalOf` for
 * all three, and on a workspace with no delivery reports it drew `Delivered 0`:
 * the quiet days in the window are real zeroes, the unreported days were
 * skipped, and the sum of the survivors was zero. A confident zero over mail
 * nobody has reported on is the exact shape of both criticals this project has
 * shipped, and it is wrong in the direction that looks healthy.
 *
 * So: one unreported day in the window and the window has no total. "At least
 * N" is not a figure this page draws, and a dash is the honest answer.
 */
function reportedTotalOf(values: readonly (number | null)[]): number | null {
  if (values.length === 0) return null;
  if (values.some((value) => value === null)) return null;
  return values.reduce((sum: number, value) => sum + (value ?? 0), 0);
}

/** A rate over a sub-window, or null where the parts were never reported. */
function rateOf(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null || whole <= 0) return null;
  return part / whole;
}

/**
 * The approved design's right-hand panel inside "How your sends are doing".
 *
 * WHY THE DAY / WEEK / MONTH CONTROL IS REAL
 * ---------------------------------------------------------------------------
 * The design draws it, so it is drawn. A segmented control that does nothing is
 * a lie told in furniture, so it works: all three folds come off the same
 * thirty daily buckets this response already carries, and no window reaches the
 * server. The heading follows the selection rather than staying at "Last 7
 * days", because a panel headed seven days while showing thirty is the defect
 * this file's own mappers exist to refuse.
 *
 * **It is not a window parameter.** `overview-activity.controller.ts` refuses
 * `?from=` on the grounds that a reader who can choose the window can choose
 * the window their bounce rate looks acceptable in. That argument is about what
 * the SERVER measures. These three are slices of one already-fixed measurement,
 * every one of them labelled, and the thirty-day rates the page judges against
 * Amazon's line are drawn elsewhere by `SendingHealthTiles` and unaffected.
 *
 * WHY EVERY ROW CAN BE A DASH
 * ---------------------------------------------------------------------------
 * Delivered, bounced and complained are all REPORTS. `emailDailyValues` answers
 * `null` for a day nothing has been reported for, and on a workspace whose
 * delivery reports are not set up that is every day. The design draws exactly
 * that state — three em-dashes and empty tracks over a sentence saying they
 * fill in from the first report — so a `0` here would not merely be wrong, it
 * would be wrong in the direction that looks healthy.
 */
export function RecentWindowPanel({
  activity,
  sending,
  initialWindow = 'week',
}: {
  activity: AsyncData<OverviewActivityResponse>;
  /**
   * Which window the panel opens on. `week` is the design's default and what
   * the page passes.
   *
   * It is a prop rather than a constant because the Month view is where the
   * thirty-day bounce and complaint rates live now — the two figures the owner
   * moved off the tiles — and with the window held in `useState` alone there
   * is no way for a static render to reach them. A figure that cannot be
   * rendered in a test is a figure with no assertion, which is how this page
   * shipped two card families and a hidden calendar.
   */
  initialWindow?: WindowKey;
  /**
   * The thirty-day readings, straight off `GET /overview`. No new request:
   * Month renders these rather than folding buckets, so the figure the removed
   * tiles printed is the same object relocated.
   */
  sending: OverviewResponse['sending'];
}) {
  const [windowKey, setWindowKey] = useState<WindowKey>(initialWindow);
  const choice = WINDOW_CHOICES.find((one) => one.key === windowKey) ?? WINDOW_CHOICES[1];

  return (
    <div data-role="recent-window" className="rounded-base border border-line bg-white/[0.02] p-[14px]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <b className="whitespace-nowrap text-[0.8rem] font-semibold">{choice.heading}</b>
        {/*
          SLICE 66: THE KIT'S SEGMENTED TOGGLE, IN ITS SMALL MONO SIZE.
          It was a hand-made row of pill buttons; it is now the one control the
          Blueprint uses wherever a value is toggled in place, so this panel and
          the rest of the product press the same way. Still `aria-pressed`
          buttons in a named group — it is not Tabs, because no panel is
          swapped, only the window the same figures are folded over. The value
          is checked back against `WINDOW_CHOICES` before it is kept, so the
          string the control hands back can never become a window this file
          does not define.
        */}
        <SegmentedToggle
          label="Window shown"
          size="sm"
          className="flex-none"
          value={windowKey}
          onValueChange={(value) => {
            const one = WINDOW_CHOICES.find((candidate) => candidate.key === value);
            if (one !== undefined) setWindowKey(one.key);
          }}
          options={WINDOW_CHOICES.map((one) => ({ value: one.key, label: one.label }))}
        />
      </div>

      <ActivityBody activity={activity} loading={`Loading the ${choice.heading.toLowerCase()}…`}>
        {(data) => {
          /*
           * MONTH RENDERS THE WIRE. DAY AND WEEK FOLD BUCKETS.
           *
           * At Month the figures are `sending`'s own already-floored,
           * already-banded readings — the very objects the two tiles used to
           * print. Nothing is recomputed, so the thirty-day rate a reader sees
           * here is literally the same number, relocated, rather than a second
           * implementation that could drift from it.
           *
           * At Day and Week the fold stays in the browser, because the server
           * publishes one window and this control is a slice of it. It uses the
           * contract's OWN builders — `rateReading` and `healthBand` — for the
           * same reason: `rateReading`'s doc says it is shared with the web app
           * precisely so two answers to "is there enough volume" cannot drift.
           *
           * Two corrections land here that the old local arithmetic had wrong:
           * the complaint denominator is `delivered + bounced` (settled), as
           * `overview-sending.ts` computes it, not `delivered` alone; and the
           * 1,000-message floor now applies, where the old `rateOf` consulted
           * no floor and would print a percentage over twelve messages that the
           * tile refused outright.
           */
          const buckets = data.channels.email.measured ? data.channels.email.buckets : [];
          const take = (measure: 'delivered' | 'bounced' | 'complained') =>
            reportedTotalOf(emailDailyValues(buckets, measure).slice(-choice.days));

          const onMonth = windowKey === 'month';

          const delivered = onMonth ? sending.deliverability.numerator : take('delivered');
          const folded = onMonth ? null : { bounced: take('bounced'), complained: take('complained') };
          const settled =
            folded === null || folded.bounced === null || delivered === null
              ? null
              : delivered + folded.bounced;

          const bounceReading = onMonth
            ? sending.bounceRate.reading
            : settled === null || folded === null || folded.bounced === null
              ? null
              : rateReading(folded.bounced, settled);
          const complaintReading = onMonth
            ? sending.complaintRate.reading
            : settled === null || folded === null || folded.complained === null
              ? null
              : rateReading(folded.complained, settled);

          const bounceBand = onMonth
            ? sending.bounceRate.band
            : bounceReading === null
              ? null
              : healthBand(bounceReading, OVERVIEW_RATE_THRESHOLDS.bounce);
          const complaintBand = onMonth
            ? sending.complaintRate.band
            : complaintReading === null
              ? null
              : healthBand(complaintReading, OVERVIEW_RATE_THRESHOLDS.complaint);

          const nothingReported = delivered === null && bounceReading === null && complaintReading === null;
          const anyRate = bounceReading !== null || complaintReading !== null;
          /*
           * Six words that stop the owner's removal costing them the judged
           * percentage. Seven days rarely clears a thousand settled results on
           * a workspace sending a few thousand a month, so without this the
           * figure simply is not reachable from the default window.
           */
          const rescue =
            !onMonth &&
            bounceReading !== null &&
            bounceReading.rate === null &&
            sending.bounceRate.reading.rate !== null;

          return (
            <div className="flex flex-col gap-3">
              <PanelCountRow label="Delivered" value={delivered} />
              <PanelRateRow
                label="Bounce rate"
                reading={bounceReading}
                threshold={OVERVIEW_RATE_THRESHOLDS.bounce}
                band={bounceBand}
              />
              <PanelRateRow
                label="Complaint rate"
                reading={complaintReading}
                threshold={OVERVIEW_RATE_THRESHOLDS.complaint}
                band={complaintBand}
              />

              {rescue && (
                <p className="text-[0.7rem] leading-relaxed text-muted">
                  The thirty-day figure is under Month.
                </p>
              )}

              {/*
                AMAZON'S TWO LINES, BESIDE THE RATES THEY JUDGE.
                These sentences came off the two tiles the owner removed. They
                are the actionable half of what those tiles said, and both
                figures are interpolated from the contract rather than typed —
                a threshold a screen states from memory is a threshold that
                goes stale the day AWS moves it.
              */}
              {anyRate && (
                <MoreDetail summary="Amazon's lines">
                  <p data-role="panel-thresholds">
                    Counts until {formatCount(OVERVIEW_MIN_VOLUME_FOR_RATE)} deliveries or bounces
                    are reported. Amazon reviews an account above{' '}
                    {formatRate(OVERVIEW_RATE_THRESHOLDS.bounce)} bounce rate, or above{' '}
                    {formatRate(OVERVIEW_RATE_THRESHOLDS.complaint)} complaint rate.
                  </p>
                </MoreDetail>
              )}

              {nothingReported && (
                <p className="rounded-base border border-dashed border-line-strong px-3 py-3 text-center text-[0.72rem] leading-relaxed text-muted">
                  These fill in, with their green / amber / red verdict against Amazon&rsquo;s line, from
                  the first delivery report.
                </p>
              )}
            </div>
          );
        }}
      </ActivityBody>
    </div>
  );
}

/**
 * One row of the panel that is a COUNT — no line, no band, no verdict.
 *
 * `Delivered` is a number of messages, not a rate, so nothing judges it and the
 * track stays empty. It keeps the track so the three rows share a baseline;
 * an empty track under a dash is this design's absent state and reads as one.
 */
function PanelCountRow({ label, value }: { label: string; value: number | null }) {
  return (
    <div data-role="panel-row" className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3 text-[0.78rem]">
        <span className="text-muted">{label}</span>
        {value === null ? (
          <NoFigure state="not_measured" meaning={`No ${label.toLowerCase()} has been reported yet.`} />
        ) : (
          <b className="text-[0.9rem] font-semibold tabular-nums text-text">
            {formatCount(value)}
          </b>
        )}
      </div>
      <div className="h-[5px] overflow-hidden rounded-full bg-white/[0.08] forced-colors:bg-[CanvasText]" />
    </div>
  );
}

/**
 * One row of the panel that is a RATE, and the home of the two figures the
 * owner took off the tiles.
 *
 * THE THRESHOLD BAR IS THE SAME COMPONENT, RELOCATED
 * ---------------------------------------------------------------------------
 * Not a recoloured fill in the 5px track this panel used before. `ThresholdBar`
 * draws Amazon's line as a **visible mark** at `scaleMax = max(threshold * 2,
 * rate * 1.2)`, and that mark is what makes an amber bar legible as "near the
 * line" rather than "quite full". A length alone cannot say which.
 *
 * It also carries three `forced-colors:` layers, and `ThresholdBar` is the
 * component that once shipped invisible in forced-colours mode because its
 * track and its fill were both backgrounds. Re-implementing the bar here would
 * have re-opened that.
 *
 * THE BAND COMES FROM THE CONTRACT, NOT FROM A LOCAL NUMBER
 * ---------------------------------------------------------------------------
 * This panel used to band on a private `share < 0.7`. The contract's own
 * `healthBand` turns amber at `OVERVIEW_WARNING_FRACTION_OF_THRESHOLD`, and one
 * page with two answers to "is this figure getting worrying" is how the two
 * come to disagree on the screen a reader is judging an account by.
 *
 * THE VERDICT IS A WORD
 * ---------------------------------------------------------------------------
 * The `Badge` under the bar is not decoration. Colour is never the only carrier
 * of a verdict here — on a greyscale screenshot, or for a reader with no colour
 * vision, "Over the line" is the whole message.
 */
function PanelRateRow({
  label,
  reading,
  threshold,
  band,
}: {
  label: string;
  reading: RateReading | null;
  threshold: number;
  band: HealthBand | null;
}) {
  if (reading === null) {
    return (
      <div data-role="panel-row" className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3 text-[0.78rem]">
          <span className="text-muted">{label}</span>
          <NoFigure state="not_measured" meaning={`No ${label.toLowerCase()} has been reported yet.`} />
        </div>
        <div className="h-[5px] overflow-hidden rounded-full bg-white/[0.08] forced-colors:bg-[CanvasText]" />
      </div>
    );
  }

  /*
   * UNDER THE FLOOR: COUNTS, AND NO TRACK.
   *
   * An empty track under a dash is an absent state. An empty track under
   * "3 of 412" is a zero — and a rate over 412 messages is the figure the tile
   * this replaced refused outright.
   */
  if (reading.rate === null || band === null) {
    return (
      <div data-role="panel-row" className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3 text-[0.78rem]">
          <span className="text-muted">{label}</span>
          <b className="text-[0.9rem] font-semibold tabular-nums text-text">
            {formatCount(reading.numerator)} of {formatCount(reading.denominator)}
          </b>
        </div>
        <StateChip state="under_floor" />
      </div>
    );
  }

  return (
    <div data-role="panel-row" className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3 text-[0.78rem]">
        <span className="text-muted">{label}</span>
        <b className={cn('text-[0.9rem] font-semibold tabular-nums', BAND_TEXT[band])}>
          {formatRate(reading.rate)}
        </b>
      </div>
      <ThresholdBar rate={reading.rate} threshold={threshold} band={band} />
      <Badge tone={BAND_VERDICT[band].tone} className="self-start">
        {BAND_VERDICT[band].label}
      </Badge>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The two cards                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The three async states, and the reason a failure here is contained.
 *
 * `GET /overview/activity` is its own request, so a failure blanks the charts
 * and leaves every tile above them — including the two rates that can cost
 * somebody their AWS account — exactly where they were. **It does not leave the
 * stopped-campaign block where it was**: that rides on this read too, which is
 * why the failure sentence names it rather than claiming the rest of the page
 * is untouched. `ErrorState` rather than
 * `FormAlert`: nothing was submitted, this is a read that did not arrive.
 */
function ActivityBody({
  activity,
  children,
  loading = 'Loading the last thirty days…',
}: {
  activity: AsyncData<OverviewActivityResponse>;
  children: (data: OverviewActivityResponse) => ReactNode;
  /**
   * The spinner's sentence. It has a default because two of this component's
   * three mounts are thirty-day charts and were byte-identical before the prop
   * existed, and an override because the third is a seven-day card: a panel
   * titled "Last 7 days" that says "Loading the last thirty days…" is the copy
   * half of the same defect its figures are careful about.
   */
  loading?: string;
}) {
  const { data, error, status, loadingFirst, reload } = activity;

  if (loadingFirst) {
    return (
      <div className="flex items-center gap-3 py-10 text-[0.9rem] text-muted">
        <Spinner size="sm" label={null} />
        <span role="status">{loading}</span>
      </div>
    );
  }

  if (status === 'error' && data === null) {
    return (
      <div className="flex flex-col gap-3">
        {/*
          NOT ANNOUNCED, AND THIS IS THE ONLY PLACE THAT DECIDES IT.

          `ActivityBody` is mounted three times on the Overview - once by each
          chart card and once by `LastSevenDaysCard` - and all three ride the
          same `GET /overview/activity`. `AttentionSlot` rides it too, above
          them, unconditionally. So one failed request put four `role="alert"`
          regions on the page, three of them word for word identical, and a
          screen reader read the same sentence four times before the reader
          reached any of the page.

          The attention slot is the announcer, because it is first in the
          document and because what it says is the urgent half: a campaign may
          have stopped and Wend could not check. These two say the figures did
          not load, which is visible in the place the figures would have been.

          Nothing a sighted reader sees changes: the heading, the message, the
          request id and the retry are all still here, in both cards.
        */}
        <ErrorState
          error={error}
          announce={false}
          title="The daily figures did not load"
          onRetry={reload}
          className="flex flex-col items-start gap-3 rounded-base border border-line px-5 py-6"
        />
        {/* It used to add "The check for stopped campaigns came from the same
            request. Nothing else on this page did." — false in both cards that
            print this, since each chart card comes from the same request as the
            other, and about request plumbing a reader has no use for. The
            stopped-campaign slot says for itself that it could not check. */}
      </div>
    );
  }

  if (data === null) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* `announce={false}` for the same two-cards-one-read reason as above. */}
      {status === 'error' && (
        <ErrorState
          error={error}
          announce={false}
          title="Could not refresh the daily figures"
          onRetry={reload}
          className="flex flex-col items-start gap-3 rounded-base border border-line px-5 py-4"
        />
      )}
      {children(data)}
    </div>
  );
}

/** Above the fold: what this workspace has been sending. */
export function ActivityVolumeCard({
  activity,
  deliveryReports,
}: {
  activity: AsyncData<OverviewActivityResponse>;
  /** `GET /overview`'s, so the line under the chart is the tiles' sentence. Null on an older API. */
  deliveryReports: DeliveryReports | null;
}) {
  return (
    <Card>
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]">Volume</CardTitle>
        <p className="text-[0.82rem] leading-relaxed text-muted">
          {/* The window off the response once it has answered, and the constant
              both sides compute it from until then — never a literal 30. The
              two cannot disagree, because the API resolves the window from this
              same constant. */}
          Messages a provider accepted, over the last{' '}
          {activity.data?.window.days ?? OVERVIEW_ACTIVITY_WINDOW_DAYS} days.
          {/* Its own read time, because the page's opening line says the charts
              carry one — and for a while they did not (review). */}
          {activity.data !== null && <> Read at {formatDateTime(activity.data.generatedAt)}.</>}
        </p>
      </CardHeader>
      <CardContent className="p-[18px] pt-0">
        <ActivityBody activity={activity}>
          {(data) => <VolumeChart activity={data} deliveryReports={deliveryReports} />}
        </ActivityBody>
      </CardContent>
    </Card>
  );
}

/** Below it: what came back, against the lines Amazon draws. */
/**
 * Whether either channel has an outcome worth drawing.
 *
 * The same two questions the card's own body asks before it mounts a chart:
 * the channel must be measured, and something must have been reported about it.
 * Asked here as well so the decision to draw the card at all and the decision
 * to draw a chart inside it cannot disagree.
 */
function chartableChannel(data: OverviewActivityResponse): boolean {
  const { email, whatsapp } = data.channels;
  return (
    (email.measured && !noOutcomeReported(email.totals)) ||
    (whatsapp.measured && !noOutcomeReported(whatsapp.totals))
  );
}

export function ActivityOutcomesCard({
  activity,
  deliveryReports,
}: {
  activity: AsyncData<OverviewActivityResponse>;
  /** `GET /overview`'s, so an unreported channel is explained in the tiles' words. Null on an older API. */
  deliveryReports: DeliveryReports | null;
}) {
  /*
   * NEITHER CHANNEL CAN BE CHARTED, SO THERE IS NO CARD — SLICE 26.
   *
   * On the deployment that actually sends, this card rendered **no figure and
   * no chart at all**: a dashed box with a chip, the sentence *"Wend has never
   * received a delivery report from Amazon for this workspace"*, and a
   * disclosure — and the identical sentence was visibly on the not-delivered
   * card too. `verify-dashboard`'s repetition probe could not see it, because a
   * chip with no full stop welded itself to the front of one copy; fixing the
   * probe to split on block boundaries is what surfaced it.
   *
   * A card that draws nothing is not a chart card, it is a second place the
   * page says one thing. The tiles above say it once, in a chip, with the cause
   * in their disclosure. This mounts again the moment either channel has a day
   * worth drawing — nothing about the charts changed, and the two threshold
   * lines that make rule 3 work are waiting intact.
   *
   * Only the loaded case is gated: a failed or pending read still renders, or
   * the card would vanish exactly when it has something to say about itself.
   */
  const data = activity.data;
  if (data !== null && !chartableChannel(data)) return null;

  return (
    <Card>
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]">Outcomes</CardTitle>
        {/* It used to describe "the dashed line", always — over a card that
            often draws none — and to call it Amazon's threshold "for that day",
            which Amazon does not have. The line explains itself where it is
            drawn. */}
        <p className="text-[0.82rem] leading-relaxed text-muted">
          What Amazon and Meta reported back about each day's messages.
          {activity.data !== null && <> Read at {formatDateTime(activity.data.generatedAt)}.</>}
        </p>
      </CardHeader>
      <CardContent className="p-[18px] pt-0">
        <ActivityBody activity={activity}>
          {(data) => {
            const { email, whatsapp } = data.channels;
            /* Asked before a chart is mounted. See the header's fourth thing a
               zero can be: a measured channel with nothing reported gets the
               sentence, not a threshold line over a row of nulls. */
            const emailCharted = email.measured && !noOutcomeReported(email.totals);
            const whatsappCharted = whatsapp.measured && !noOutcomeReported(whatsapp.totals);
            /* The bounce line stands for both: the complaint line is drawn on
               exactly the same days. */
            const emailLineDrawn =
              emailCharted &&
              emailThresholdLine(email.buckets, 'bounce').values.some((value) => value !== null);
            /* Whether bars are drawn, not merely whether a channel has reports.
               A clean month under the floor draws two "none bounced" boxes and
               no bars, and notes about bars over no bars are the boilerplate
               `chartNotes` exists to avoid (review). */
            const emailBarsDrawn =
              emailCharted &&
              (emailOutcomeScale(email.buckets, 'bounced', 'bounce').axisMax !== null ||
                emailOutcomeScale(email.buckets, 'complained', 'complaint').axisMax !== null);
            const whatsappBarsDrawn =
              whatsappCharted &&
              chartAxisMax([whatsAppUndeliverableSeries(whatsapp.buckets)]) !== null;
            return (
              <div className="flex flex-col gap-6">
                {email.measured && !emailCharted ? (
                  <NoOutcomeReported
                    explanation={unreportedExplanation(
                      'email',
                      deliveryReports,
                      email.totals.complained,
                    )}
                  />
                ) : email.measured ? (
                  /* Stacked, never side by side, and this is a measurement
                     rather than a taste. `TimeSeriesChart` draws into a
                     760-unit viewBox at `w-full`, so its 11-unit axis type
                     scales with the container: at full card width that is
                     legible, and in a half-width column it renders at about
                     seven and a half pixels. A chart whose axis labels cannot
                     be read is a chart with no scale. */
                  <div className="flex flex-col gap-8">
                    <EmailOutcomeChart
                      buckets={email.buckets}
                      measure="bounced"
                      which="bounce"
                      heading="Bounced — email"
                      reportedTotal={email.totals.bounced}
                      deliveryReports={deliveryReports}
                      accessibleName={{
                        withLine: "Email that bounced, per day, against Amazon's bounce review line where a day's reported outcomes allow one",
                        withoutLine: 'Email that bounced, per day. No review line: no day had enough reported deliveries and bounces for one.',
                      }}
                    />
                    <EmailOutcomeChart
                      buckets={email.buckets}
                      measure="complained"
                      which="complaint"
                      heading="Reported as spam — email"
                      reportedTotal={email.totals.complained}
                      deliveryReports={deliveryReports}
                      accessibleName={{
                        withLine: "Email reported as spam, per day, against Amazon's complaint review line where a day's reported outcomes allow one",
                        withoutLine: 'Email reported as spam, per day. No review line: no day had enough reported deliveries and bounces for one.',
                      }}
                    />
                    {!emailLineDrawn && (
                      <p className="text-[0.76rem] leading-relaxed text-muted">
                        {EMAIL_CHART_THRESHOLD_FLOOR_COPY}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-[0.85rem] leading-relaxed text-muted">{email.message}</p>
                )}

                {whatsapp.measured && !whatsappCharted ? (
                  <NoOutcomeReported
                    explanation={unreportedExplanation('whatsapp', deliveryReports, 0)}
                  />
                ) : whatsapp.measured ? (
                  <WhatsAppOutcomeChart buckets={whatsapp.buckets} deliveryReports={deliveryReports} />
                ) : (
                  <p className="text-[0.85rem] leading-relaxed text-muted">{whatsapp.message}</p>
                )}

                {/* Once for the card rather than once per chart. Both charts are
                    cut from the same buckets over the same window, so the same
                    four sentences apply to both, and printing them twice under
                    a side-by-side pair is how a reader learns that the text
                    under a chart is boilerplate. */}
                {/* Only when a chart is drawn, and from a channel that IS
                    drawn. The first version read the email buckets whenever
                    email was measured, so an unreported email channel beside
                    a drawn WhatsApp chart withheld the settling note from the
                    chart it applied to; and with no chart at all it still
                    printed the zone note and a disclosure about "these bars"
                    over no bars — the boilerplate `chartNotes` exists to
                    avoid. */}
                {(emailBarsDrawn || whatsappBarsDrawn) && (() => {
                  /* Asked of every channel whose bars are drawn, together. It
                     read email's buckets alone whenever email was charted, so
                     WhatsApp still awaiting a result beside settled email got
                     no settling note (review). */
                  const dayNotes = chartNotes(
                    [
                      ...(emailBarsDrawn ? email.buckets : []),
                      ...(whatsappBarsDrawn ? whatsapp.buckets : []),
                    ],
                    true,
                  );
                  return (
                    <>
                      <Notes notes={[...(emailBarsDrawn ? [NO_DAILY_RATE_COPY.short] : []), ...dayNotes]} />
                      <ActivityDetail
                        notes={dayNotes}
                        hasThresholds={emailBarsDrawn}
                        hasWhatsAppChart={whatsappBarsDrawn}
                      />
                    </>
                  );
                })()}
              </div>
            );
          }}
        </ActivityBody>
      </CardContent>
    </Card>
  );
}
